import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["width"];
import { useCallback, useMemo } from 'react';
import { PropertyEditCell } from 'framework-data-table';
import { isDate } from 'customer-data-objects/property/PropertyIdentifier';
import { getPropertyValue, setPropertyValue } from 'framework-listing-lib/utils/crmObject';
import { useGetInlineEditPropertyValue, useGetPropertyValidation, useOnPropertyValueChange, useOnResetPropertyValue, useOnValidationChange } from 'framework-listing-lib/internal/FrameworkDataTable/hooks/useInlineEdit';
import ListingLibDateTimeCell from 'framework-listing-lib/table/ListingLibDateTimeCell';
import { useTableColumn } from 'framework-listing-lib/internal/Table/providers/TableColumnsProvider';
import TableRowProvider, { useTableRow } from 'framework-listing-lib/internal/FrameworkDataTable/providers/TableRowProvider';
import ListingLibEmptyStateCell from 'framework-listing-lib/table/ListingLibEmptyStateCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function ListingLibCellContentComponent({
  value,
  propertyDefinition,
  getSecondaryPropertyValue
}) {
  let cellComponent;
  const column = useTableColumn(propertyDefinition.name);
  const {
    row,
    tdProps
  } = useTableRow();
  const crmObject = setPropertyValue(row, column.id, value);
  if (!column) {
    return /*#__PURE__*/_jsx(ListingLibEmptyStateCell, {});
  }
  if (column.renderCell) {
    cellComponent = column.renderCell({
      crmObject,
      propertyName: column.id,
      propertyDefinition,
      tdProps
    });
  } else if (isDate(column.propertyDescriptor)) {
    cellComponent = /*#__PURE__*/_jsx(ListingLibDateTimeCell, {
      crmObject: crmObject,
      propertyName: column.id,
      value: value,
      getSecondaryPropertyValue: getSecondaryPropertyValue
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: cellComponent
  });
}
export default function ListingLibInlineEditCell({
  column,
  columnNamespace,
  requiredCellProps,
  row,
  tdProps
}) {
  const onPropertyValueChange = useOnPropertyValueChange();
  const onResetPropertyValue = useOnResetPropertyValue();
  const onValidationChange = useOnValidationChange();
  const getInlineEditPropertyValue = useGetInlineEditPropertyValue();
  const getPropertyValidation = useGetPropertyValidation();
  const originalValue = useMemo(() => {
    const propertyValue = column ? getPropertyValue(row, column.id, '') : '';
    if (!propertyValue) {
      return '';
    }
    return `${propertyValue}`;
  }, [column, row]);
  const getSecondaryPropertyValue = useCallback(name => {
    const savedValue = getPropertyValue(row, name);
    return getInlineEditPropertyValue(String(row.objectId), name, savedValue);
  }, [row, getInlineEditPropertyValue]);
  const cellProps = _objectWithoutPropertiesLoose(tdProps, _excluded);

  /**
   * Use our custom cell renderer when using a custom cell (defined by teams)
   * or when rendering a date|datetime cell.
   */
  const hasCustomCellOrIsDate = Boolean(column.renderCell || isDate(column.propertyDescriptor));
  return /*#__PURE__*/_jsx(TableRowProvider, {
    row: row,
    tdProps: tdProps,
    children: /*#__PURE__*/_jsx(PropertyEditCell, {
      CellContentComponent: hasCustomCellOrIsDate ? ListingLibCellContentComponent : undefined,
      objectId: String(row.objectId),
      objectTypeId: columnNamespace,
      value: getInlineEditPropertyValue(String(row.objectId), column.id, originalValue),
      propertyDefinition: column.propertyDescriptor,
      canEditObject: true,
      getSecondaryPropertyValue: getSecondaryPropertyValue,
      requiredCellProps: Object.assign({}, cellProps, requiredCellProps),
      originalValue: originalValue,
      onPropertyUpdates: updates => {
        if (updates.values[column.id] === originalValue) {
          onPropertyValueChange(updates);
          onResetPropertyValue(String(row.objectId), column.id);
        } else {
          onPropertyValueChange(updates);
        }
      },
      onValidationChange: validation => {
        onValidationChange(String(row.objectId), column.id, validation);
      },
      validationResult: getPropertyValidation(String(row.objectId), column.id),
      enableConditionalPropertiesPanel: true,
      enableConditionalPropertyOptions: true
    })
  });
}